import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['Crypto Exchange', 'web3.js', 'Ether.js', 'Bitcoin Node', 'Trading', 'React.js', 'Crypto Wallet', 'Solidity' , 'Smart Contract', 'Next.js', 'NestJS', 'ExpressJS', 'API Integration', 'React Native', 'Ethereum', 'AWS', 'Firebase', 'NFT minting', 'Bridge', 'iOS', 'Flutter', 'PostgreSQL', 'Figma', 'GraphQL', 'SASS', 'SCSS', 'TailwindCSS'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
            Looking for code that’s copy-pasted by an amateur and is full of security vulnerabilities? Yeah, didn’t think so.
            You want an expert you trust to handle the most critical component of your project.
            I am a seasoned full stack developer with over a decade of experience, mastering a diverse toolkit that includes ReactJS, NextJS, ExpressJS, and more. My frontend development capabilities in ReactJS and NextJS are complemented by my backend proficiency in ExpressJS and NextJS, allowing me to create <a>robust and scalable applications</a>. Notably, I excel at seamless API integrations and have a knack for <a>optimizing performance and user experience.</a>
            Transitioning into the realm of blockchain over the past <a>4 years</a>, I've spearheaded a range of web3 integration projects. My expertise extends to web3.js, enabling seamless blockchain connections, and I'm adept at utilizing Redis for cache databases. Furthermore, my blockchain experience is enriched by my understanding of <a>smart contract development, decentralized applications (DApps)</a>, and layer 2 scaling solutions. I also possess a strong foundation in cryptography, ensuring data security and transaction integrity in all my projects.
            Simply put, I’m an expert. So if you’re looking for someone to guide you through this world, give you <a>fast and reliable deliveries</a> (with full transparency), and make sense of all of the technical jargon for you, <a>I’m your guy.</a>
            </p>
            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
